import { Modal } from "shared/components/Modal";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";

import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import {
  CreateLeadPayload,
  useFormController
} from "shared/app/LeadsTableView/components/CreateLeadModal/useFormController";
import { BasicInfoSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/BasicInfo";
import { ContactInfoSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/ContactInfo";
import { InterestedInSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/InterestedIn";
import { DetailsSection } from "shared/app/LeadsTableView/components/CreateLeadModal/sections/Details";
import { Footer } from "shared/app/LeadsTableView/components/CreateLeadModal/Footer";

export const CreateLeadModal = ({
  open,
  onClose,
  refetchLeads
}: {
  open: boolean;
  onClose: () => void;
  refetchLeads: () => void;
}) => {
  const form = useFormController();

  const { mutateAsync: createLead, isPending } = useMutation({
    mutationKey: ["CREATE_LEAD"],
    mutationFn: async (values: CreateLeadPayload) => {
      // Add "1" prefix to all phone numbers
      const processedContactMethods = [
        { type: "phone", value: `1${values.primary_phone}`, is_primary: true },
        { type: "email", value: values.primary_email, is_primary: true },
        ...values.other_phones
          .filter((phone) => phone.trim() !== "")
          .map((phone) => ({
            type: "phone",
            value: `1${phone}`,
            is_proimary: false
          })),
        ...values.other_emails
          .filter((email) => email.trim() !== "")
          .map((email) => ({ type: "email", value: email, is_proimary: false }))
      ];

      return instance.post("/portal/v1/leads", {
        ...values,
        contact_methods: processedContactMethods,
        source_id: Number(values.source_id) || undefined,
        partner_user_id: values.partner_user_id || undefined
      });
    }
  });

  const handleClose = () => {
    onClose();
    form.reset();
  };

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      panelClassName="w-full max-w-[816px] p-0 overflow-y-hidden mx-5"
    >
      <form
        onSubmit={form.onSubmit({
          onSuccess: () => {
            createLead(form.values as CreateLeadPayload).then(() => {
              refetchLeads();
              handleClose();
            });
          }
        })}
        className="flex flex-col max-h-[85vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-alpha-100 bg-white sticky top-0 z-10">
          <div>
            <h3 className="text-xl leading-6 font-bold text-content-1000">
              Create lead
            </h3>
          </div>
          <IconButton
            aria-label="Close"
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            HugeIcon={Cancel01Icon}
            onClick={handleClose}
          />
        </div>
        {/* Body (scroll area) */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6 bg-background-900">
          <BasicInfoSection register={form.register} />
          <ContactInfoSection form={form} />
          <InterestedInSection form={form} />
          <DetailsSection form={form} />
        </div>
        <Footer onClose={handleClose} isPending={isPending} />
      </form>
    </Modal>
  );
};
